import type { ESize } from '@common/enums';
import type { SelectedFileExtended } from '@common/types/file.type';
import type { WebLegacyWidgetEntity } from '@web/types';
import type {
    CarouselOptions,
    CollageOptions,
    CollageType,
    GalleryWidgetData,
    GridOptions,
} from '@web/widgets/gallery/type/gallery-widget.type';
import type { JssStyle } from 'jss';

import {
    EConditionType,
    EFontStyle,
    EGalleryTypes,
    EHorizontalPosition,
    EOverlayType,
    EVerticalPosition,
} from '@common/enums';
import { commonFontSizeCoefficient, themePStyles } from '@sites/site-theme/src/styles/text.styles';
import { collageMetadata } from '@web/widgets/gallery/model/defaultOptions';

interface GalleryReturnType {
    gridContainer: JssStyle;
    gridItem: JssStyle;
    gridImage: JssStyle;
    imageWrapper: JssStyle;
    [other: string]: JssStyle;
}

const textPosition = {
    [EHorizontalPosition.L]: 'left',
    [EHorizontalPosition.M]: 'center',
    [EHorizontalPosition.R]: 'right',
};

function getFontStyles(fontStyle: EFontStyle[]): JssStyle {
    return {
        fontWeight: fontStyle?.includes(EFontStyle.B) ? 'bold' : 'initial',
        fontStyle: fontStyle?.includes(EFontStyle.I) ? 'italic' : 'initial',
        textTransform: fontStyle?.includes(EFontStyle.AT) ? 'uppercase' : 'initial',
    };
}

function getDescriptionStyles(image: SelectedFileExtended): JssStyle {
    let commonDescription: JssStyle = {
        position: 'absolute',
        width: '100%',
        display: 'flex',
        textAlign: 'center',
        alignItems: 'center',
        wordBreak: 'break-word',
        boxSizing: 'border-box',
        padding: image?.description
            ? `${image.paddingTop}px ${image.paddingRight}px ${image.paddingBottom}px ${image.paddingLeft}px`
            : '0',
        background: image?.textBackground,
    };

    if (image?.condition === EConditionType.ON_HOVER && image?.isImageEffect) {
        commonDescription = {
            ...commonDescription,
            ...{
                opacity: 0,
                transition: 'opacity 1s linear',
            },
        };
    }

    if (!image?.description) {
        return {
            ...commonDescription,
            bottom: 0,
            left: 0,
        };
    }

    // TODO: REFACTOR TO FLEX
    const descriptionPositionStyles: Record<EVerticalPosition, JssStyle> = {
        [EVerticalPosition.B]: {
            bottom: 0,
            left: 0,
        },
        [EVerticalPosition.M]: {
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
        },
        [EVerticalPosition.T]: {
            top: 0,
            left: 0,
        },
    };
    return {
        ...commonDescription,
        ...descriptionPositionStyles[image.textPosition],
    };
}

function calcFontSize(size: ESize): string {
    return `calc(${themePStyles.fontSize} * ${commonFontSizeCoefficient[size]})`;
}

const justifyStyles = {
    [EHorizontalPosition.L]: 'flex-start',
    [EHorizontalPosition.M]: 'center',
    [EHorizontalPosition.R]: 'flex-end',
};

function getTextStyles(image: SelectedFileExtended): JssStyle {
    if (!image?.description) {
        return {};
    }

    const fontSizeCoefficient: number = commonFontSizeCoefficient[image.textSize];
    const scaledFontSize: string = image.fontSize.toString();
    const fontStyles: JssStyle = getFontStyles(image?.textStyle);
    const textStyles: JssStyle = { ...themePStyles, fontSize: scaledFontSize + 'px', color: image.textColor };
    const marginLeft: string = image.link ? '16px' : '0';
    const marginRight: string = image.link ? `calc(${themePStyles.fontSize} * ${fontSizeCoefficient} + 15px)` : '0';

    return {
        display: 'flex',
        flex: '1',
        justifyContent: justifyStyles[image.textJustify],
        textAlign: textPosition[image.textJustify],
        marginRight,
        marginLeft,
        ...textStyles,
        ...fontStyles,
    };
}

function getImageMaskStyles(image: SelectedFileExtended): JssStyle {
    let imageMaskStyles = {};

    if (!image) {
        return imageMaskStyles;
    }

    if (image?.maskShape) {
        imageMaskStyles = { WebkitMaskImage: `url('assets/img/websites/mask-shapes/${image?.maskShape}.svg')` };
    }

    return imageMaskStyles;
}

const mobileMediaQuery = '@media (max-width: 480px)';
const tabletMediaQuery = '@media (min-width: 481px) and (max-width: 992px)';
const desktopMediaQuery = '@media (min-width: 993px)';

// приховування по девайсах — лише поза конструктором (на опублікованому сайті)
function applyHideOnDeviceStyles(target: JssStyle, widgetData: GalleryWidgetData): void {
    if (widgetData?.hideOnMobile) {
        target[mobileMediaQuery] = { display: 'none' };
    }

    if (widgetData?.hideOnTablet) {
        target[tabletMediaQuery] = { display: 'none' };
    }

    if (widgetData?.hideOnDesktop) {
        target[desktopMediaQuery] = { display: 'none' };
    }
}

const commonCollageTransition = {
    transitionProperty: 'margin, padding, row-gap',
    transitionDuration: '0.25s',
    transitionTimingFunction: 'ease-out',
};

const commonImageStyles = (
    widgetData: GalleryWidgetData,
    borderRadius: number,
    clickable: boolean,
    isConstructor = false
) => {
    // висота thumbs для опублікованого сайту — через реальні media queries (по девайсах)
    const carouselOptions = widgetData.options as CarouselOptions;
    const desktopThumbHeight = carouselOptions?.previewImageHeight || 240;
    const tabletThumbHeight = carouselOptions?.previewImageHeightTablet ?? desktopThumbHeight;
    const mobileThumbHeight = carouselOptions?.previewImageHeightMobile ?? desktopThumbHeight;

    const styles: Record<'gridImage' | 'imageWrapper' | 'thumbWrapper' | 'thumbImage', JssStyle> = {
        gridImage: widgetData.images.length
            ? {
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  inset: 0,
                  objectFit: !widgetData.options?.attachment ? 'cover' : widgetData.options?.attachment,
                  backgroundColor: widgetData.options?.backgroundColor || 'transparent',
                  userSelect: 'none',
              }
            : { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
        imageWrapper: {
            position: 'relative',
            overflow: 'hidden',
            height: '100%',
            borderRadius,
            paddingTop: '100%',
            cursor: clickable ? 'pointer' : 'default',
        },
        thumbWrapper: {
            position: 'relative',
            overflow: 'hidden',
            height: '100%',
            borderRadius,
            maxHeight: desktopThumbHeight,
            [tabletMediaQuery]: {
                maxHeight: tabletThumbHeight,
            },
            [mobileMediaQuery]: {
                maxHeight: mobileThumbHeight,
            },
        },
        thumbImage: {
            width: '100%',
            height: '100%',
            objectFit: !widgetData.options?.previewAttachment ? 'cover' : widgetData.options?.previewAttachment,
            backgroundColor: widgetData.options?.backgroundColor || 'transparent',
            userSelect: 'none',
        },
    };

    if (!widgetData.images.length) {
        styles.imageWrapper.height = '200px';
        styles.imageWrapper.background = '#F8FDFE';

        if (!isConstructor) {
            applyHideOnDeviceStyles(styles.imageWrapper, widgetData);
        }
    }

    return styles;
};

const getImageStyle = (image: SelectedFileExtended): JssStyle => {
    let imageStyles: JssStyle = {};

    if (image?.isImageEffect) {
        if (image?.overlay === EOverlayType.INCREASE) {
            imageStyles = {
                ...imageStyles,
                ...{
                    transition: 'all .3s ease-in-out',
                    '&:hover': {
                        transform: 'scale(1.1)',
                    },
                },
            };
        }

        if (image?.overlay === EOverlayType.DECREASE) {
            imageStyles = {
                ...imageStyles,
                ...{
                    transition: 'all .3s ease-in-out',
                    '&:hover': {
                        transform: 'scale(0.9)',
                    },
                },
            };
        }
    }

    return imageStyles;
};

const defaultDescriptionStyles = (widgetData: GalleryWidgetData): Record<string, JssStyle> => {
    const styles = {};

    widgetData.images.forEach((image, index) => {
        styles[`imageDescription${index}`] = getDescriptionStyles(image);
        styles[`text${index}`] = getTextStyles(image);
        styles[`imageMask${index}`] = getImageMaskStyles(image);
        styles[`image${index}`] = getImageStyle(image);
    });
    return styles;
};

const masonryDescriptionStyles = (widgetData: GalleryWidgetData): Record<string, JssStyle> => {
    const columns = (widgetData.options as CollageOptions).style === '2col-2row' ? 2 : 3;
    const arr = [...Array(columns)].map(() => []);

    for (let i = 0; i < widgetData.images.length; i++) {
        arr[i % columns].push(widgetData.images[i]);
    }

    const styles = {};
    arr.forEach((column, columnIndex) => {
        column.forEach((image, imageIndex) => {
            styles[`imageDescription${columnIndex}-${imageIndex}`] = getDescriptionStyles(image);
            styles[`text${columnIndex}-${imageIndex}`] = getTextStyles(image);
            styles[`imageMask${columnIndex}-${imageIndex}`] = getImageMaskStyles(image);
            styles[`image${columnIndex}-${imageIndex}`] = getImageStyle(image);
        });
    });

    return styles;
};

const calcImageRemainder = (style: CollageType, imagesCount: number) =>
    imagesCount % collageMetadata[style]?.itemPerLine;

const generateCollageTailStyles = (style: CollageType, imagesRemainder: number): JssStyle => {
    const { gridColumns } = collageMetadata[style];

    const styles = {};
    if (imagesRemainder === 0) return styles;

    for (let i = 0; i < imagesRemainder; i++) {
        styles[`&:nth-last-child(${i + 1})`] = {
            gridRow: 'auto !important',
            gridColumn: `span ${gridColumns / imagesRemainder} !important`,
        };
    }

    return styles;
};

const collageStyles = (widgetData: GalleryWidgetData, isConstructor = false): GalleryReturnType => {
    const { imageGap: gap, borderRadius, style, clickable } = widgetData.options as CollageOptions;

    const gapMobile = (widgetData.options as GridOptions).imageGapMobile ?? gap;
    const gapTablet = (widgetData.options as GridOptions).imageGapTablet ?? gap;
    const { gridColumns } = collageMetadata[style];

    const halfGap = gap / 2;
    const quarterGap = gap / 4;

    const halfGapMobile = gapMobile / 2;
    const halfGapTablet = gapTablet / 2;

    const imagesRemainder = calcImageRemainder(style, widgetData.images.length);
    const collageTailStyles = generateCollageTailStyles(style, imagesRemainder);

    const isMasonry = ['masonry', '2col-2row'].includes(style);
    const masonryColumns = style === '2col-2row' ? 2 : 3;

    const defaultItemStyles: JssStyle = {
        extend: commonCollageTransition,
        padding: `0 ${halfGap}px`,
        position: 'relative',
        '&.mobileGridItem': {
            padding: `0 ${halfGapMobile}px`,
            position: 'relative',
        },
        '&.tabletGridItem': {
            padding: `0 ${halfGapTablet}px`,
            position: 'relative',
        },
        [mobileMediaQuery]: {
            padding: `0 ${halfGapMobile}px`,
            position: 'relative',
        },
        [tabletMediaQuery]: {
            padding: `0 ${halfGapTablet}px`,
            position: 'relative',
        },

        ...collageTailStyles,
    };

    const autoRowsCondition =
        imagesRemainder === 0 && !(['2row-3item', '5col-2row'] as Partial<CollageType[]>).includes(style);

    const defaultContainerStyles: JssStyle = {
        extend: commonCollageTransition,
        display: 'grid',
        gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
        gridAutoRows: autoRowsCondition ? '1fr' : 'initial',
        // todo - gridTemplateRows: '1fr 1fr 2fr' - calc this dynamically base on images count needed for at least one full structure,
        margin: `0 -${halfGap}px`,
        rowGap: gap,

        '&.mobileViewWrapper': {
            margin: `0 ${-halfGapMobile}px`,
            rowGap: gapMobile,
        },
        '&.tabletViewWrapper': {
            margin: `0 ${-halfGapTablet}px`,
            rowGap: gapTablet,
        },
        [mobileMediaQuery]: {
            margin: `0 ${-halfGapMobile}px`,
            rowGap: gapMobile,
        },
        [tabletMediaQuery]: {
            margin: `0 ${-halfGapTablet}px`,
            rowGap: gapTablet,
        },
    };

    const masonryContainerStyles = {
        display: 'flex',
        columnGap: widgetData.options.imageGap,
        transition: 'gap 0.25s ease-out',
        rowGap: 'unset',
        margin: 'unset',
        '& > div': {
            display: 'flex',
            flexDirection: 'column',
            flexBasis: masonryColumns === 2 ? '50%' : '33.3333%',
            rowGap: widgetData.options.imageGap,
            transition: 'gap 0.25s ease-out',
            '&.mobileGridItem': {
                rowGap: gapMobile,
            },
            '&.tabletGridItem': {
                rowGap: gapTablet,
            },
            [mobileMediaQuery]: {
                rowGap: gapMobile,
            },
            [tabletMediaQuery]: {
                rowGap: gapTablet,
            },
        },
        '&.mobileViewWrapper': {
            columnGap: gapMobile,
        },
        '&.tabletViewWrapper': {
            columnGap: gapTablet,
        },
        [mobileMediaQuery]: {
            columnGap: gapMobile,
        },
        [tabletMediaQuery]: {
            columnGap: gapTablet,
        },
    };
    const collageContainerStyles = isMasonry ? masonryContainerStyles : defaultContainerStyles;
    const collageItemStyles = isMasonry ? {} : defaultItemStyles;
    const collageDescriptionStyles = isMasonry ? masonryDescriptionStyles : defaultDescriptionStyles;

    const collageGridStyles: Record<CollageType, any> = {
        '2col-1': {
            item: {
                gridColumn: 'span 3',

                '&:nth-child(2n)': {
                    gridColumn: 'span 2',
                },
            },
        },
        '2col-2': {
            item: {
                gridColumn: 'span 2',

                '&:nth-child(2n)': {
                    gridColumn: 'span 3',
                },
            },
        },
        '2col-3item-1': {
            item: {
                gridColumn: 'span 4',

                '&:nth-child(6n+1)': {
                    gridColumn: 'span 8',
                    gridRow: 'span 2',
                },
                '&:nth-child(6n+5)': {
                    gridColumn: 'span 8',
                    gridRow: 'span 2',
                },
            },
        },
        '2col-3item-2': {
            item: {
                gridColumn: 'span 4',

                '&:nth-child(6n+2)': {
                    gridColumn: 'span 8',
                    gridRow: 'span 2',
                },
                '&:nth-child(6n+4)': {
                    gridColumn: 'span 8',
                    gridRow: 'span 2',
                },
            },
        },
        '2row-3item': {
            item: {
                gridColumn: 'span 6',

                '&:nth-child(3n+1)': {
                    gridColumn: 'span 12',
                },
            },
        },
        '5col-2row': {
            item: {
                gridColumn: 'span 4',

                '&:nth-child(5n+1)': {
                    gridColumn: 'span 6',
                },
                '&:nth-child(5n+2)': {
                    gridColumn: 'span 6',
                },
            },
        },
        '3col-1': {
            item: {
                gridColumn: 'span 3',

                '&:nth-child(10n+1)': {
                    gridColumn: 'span 6',
                    gridRow: 'span 2',
                },
                '&:nth-child(10n+8)': {
                    gridColumn: 'span 6',
                    gridRow: 'span 2',
                },
            },
        },
        '3col-2': {
            item: {
                gridColumn: 'span 3',

                '&:nth-child(10n+3)': {
                    gridColumn: 'span 6',
                    gridRow: 'span 2',
                },
                '&:nth-child(10n+6)': {
                    gridColumn: 'span 6',
                    gridRow: 'span 2',
                },
            },
        },
        masonry: {
            item: {
                position: 'relative',
            },
        },
        '2col-2row': {
            item: {
                position: 'relative',
            },
        },
    };

    return {
        gridContainer: collageContainerStyles,
        gridItem: {
            extend: collageItemStyles,
            ...collageGridStyles[style].item,
        },
        ...commonImageStyles(widgetData, borderRadius, clickable, isConstructor),
        ...collageDescriptionStyles(widgetData),
    };
};

const gridStyles = (widgetData: GalleryWidgetData, isConstructor = false): GalleryReturnType => {
    const { imageGap: gap, rowItems, borderRadius, clickable } = widgetData.options as GridOptions;
    const halfGap = gap / 2;

    const gapMobile = (widgetData.options as GridOptions).imageGapMobile ?? gap;
    const gapTablet = (widgetData.options as GridOptions).imageGapTablet ?? gap;
    const rowItemsMobile =
        (widgetData.options as GridOptions)?.rowItemsMobile ?? (widgetData.options as GridOptions).rowItems;
    const rowItemsTablet =
        (widgetData.options as GridOptions)?.rowItemsTablet ?? (widgetData.options as GridOptions).rowItems;

    const gridContainerStyles = {
        display: 'grid',
        gap,
        transition: 'gap 0.25s ease-out',
        gridTemplateColumns: `repeat(${rowItems}, 1fr)`,
        gridAutoRows: '1fr',
        '&.mobileViewWrapper': {
            gap: gapMobile,
            gridTemplateColumns: `repeat(${rowItemsMobile}, 1fr)`,
        },
        '&.tabletViewWrapper': {
            gap: gapTablet,
            gridTemplateColumns: `repeat(${rowItemsTablet}, 1fr)`,
        },
        [mobileMediaQuery]: {
            gap: gapMobile,
            gridTemplateColumns: `repeat(${rowItemsMobile}, 1fr)`,
        },
        [tabletMediaQuery]: {
            gap: gapTablet,
            gridTemplateColumns: `repeat(${rowItemsTablet}, 1fr)`,
        },
    };

    const gridItemStyles = {
        position: 'relative',
    };

    return {
        gridContainer: gridContainerStyles,
        gridItem: gridItemStyles,
        ...commonImageStyles(widgetData, borderRadius, clickable, isConstructor),
        ...defaultDescriptionStyles(widgetData),
    };
};

const styles = (widgetData: GalleryWidgetData, isConstructor = false): JssStyle => {
    const { imageGap: gap, borderRadius, clickable } = widgetData.options as CarouselOptions;
    if (widgetData.type === EGalleryTypes.collage) {
        return collageStyles(widgetData, isConstructor);
    }
    if (widgetData.type === EGalleryTypes.grid) {
        return gridStyles(widgetData, isConstructor);
    }

    const style: Record<'carousel', GalleryReturnType> = {
        carousel: {
            gridItem: {},
            gridContainer: {},
            thumbsSwiperContainer: {
                marginTop: gap,
            },
            ...commonImageStyles(widgetData, borderRadius, clickable, isConstructor),
            ...defaultDescriptionStyles(widgetData),
        },
    };
    return style[widgetData.type] || {};
};

const getGalleryStyles = (widgetData: GalleryWidgetData, isConstructor = false) => {
    let horizontalAlignValue: string = '';

    switch (widgetData?.horizontalAlign) {
        case EHorizontalPosition.L:
            horizontalAlignValue = '0 auto 0 0';
            break;
        case EHorizontalPosition.R:
            horizontalAlignValue = '0 0 0 auto';
            break;
        default:
            horizontalAlignValue = '0 auto';
    }

    const wrapperStyles: JssStyle = {
        margin: horizontalAlignValue,
        maxWidth: widgetData?.maxWidth + 'px',
        position: 'relative',
        '& .swipper-media-wrapper': {
            '&.mobileViewWrapper': {
                '& .swiper-button-prev:after, .swiper-button-next:after': {
                    fontSize: 'var(--swiper-navigation-size-mobile)',
                },
            },
            '&.tabletViewWrapper': {
                '& .swiper-button-prev:after, .swiper-button-next:after': {
                    fontSize: 'var(--swiper-navigation-size-tablet)',
                },
            },
            [mobileMediaQuery]: {
                '& .swiper-button-prev:after, .swiper-button-next:after': {
                    fontSize: 'var(--swiper-navigation-size-mobile)',
                },
            },
            [tabletMediaQuery]: {
                '& .swiper-button-prev:after, .swiper-button-next:after': {
                    fontSize: 'var(--swiper-navigation-size-tablet)',
                },
            },
        },
        '& .outsideArrowWrapper': {
            margin: '0 ' + (widgetData?.options as CarouselOptions).arrowSize + 'px',

            '&.mobileViewWrapper': {
                margin: '0 ' + (widgetData?.options as CarouselOptions).arrowSizeMobile + 'px',
            },
            '&.tabletViewWrapper': {
                margin: '0 ' + (widgetData?.options as CarouselOptions).arrowSizeTablet + 'px',
            },
            [mobileMediaQuery]: {
                margin: '0 ' + (widgetData?.options as CarouselOptions).arrowSizeMobile + 'px',
            },
            [tabletMediaQuery]: {
                margin: '0 ' + (widgetData?.options as CarouselOptions).arrowSizeTablet + 'px',
            },
        },
        '& .swiper-button-prev-sp-custom, .swiper-button-next-sp-custom': {
            color: (widgetData.options as CarouselOptions).knobsColor,
            '&:after': {
                fontSize: (widgetData?.options as CarouselOptions).arrowSize + 'px',
                textShadow: '0px 0px 4px rgb(0 0 0 / 12%)',
            },

            [mobileMediaQuery]: {
                '&:after': {
                    fontSize: (widgetData?.options as CarouselOptions).arrowSizeMobile + 'px',
                },
            },
            '&.mobileCustomBtn': {
                '&:after': {
                    fontSize: (widgetData?.options as CarouselOptions).arrowSizeMobile + 'px',
                },
            },
            [tabletMediaQuery]: {
                '&:after': {
                    fontSize: (widgetData?.options as CarouselOptions).arrowSizeTablet + 'px',
                },
            },
            '&.tabletCustomBtn': {
                '&:after': {
                    fontSize: (widgetData?.options as CarouselOptions).arrowSizeTablet + 'px',
                },
            },
        },
        '& .swiper-button-prev-sp-custom': {
            [mobileMediaQuery]: {
                left: '-5px',
            },
            '&.mobileCustomBtn': {
                left: '-5px',
            },
            [tabletMediaQuery]: {
                left: '3px',
            },
            '&.tabletCustomBtn': {
                left: '3px',
            },
        },
        '& .swiper-button-next-sp-custom': {
            [mobileMediaQuery]: {
                right: '-5px',
            },
            '&.mobileCustomBtn': {
                right: '-5px',
            },
            [tabletMediaQuery]: {
                right: '3px',
            },
            '&.tabletCustomBtn': {
                right: '3px',
            },
        },
    };

    if (!isConstructor) {
        applyHideOnDeviceStyles(wrapperStyles, widgetData);
    }

    return wrapperStyles;
};

export function getStyles(
    widget: WebLegacyWidgetEntity<GalleryWidgetData>,
    config?: { isConstructor?: boolean; isSpecific?: boolean }
): JssStyle {
    return {
        ...styles(widget.data, config?.isConstructor),
        galleryWrapper: getGalleryStyles(widget.data, config?.isConstructor),
    };
}
